import { useEffect } from 'react';
import { Box } from '@mui/material';
import { ExternalUrls } from '../../lib/constants';

interface CalendlyWidgetProps {
  url?: string;
  styles?: {
    height?: string;
    minWidth?: string;
  };
}

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (options: {
        url: string;
        parentElement: Element;
        prefill?: Record<string, string>;
        utm?: Record<string, string>;
      }) => void;
    };
  }
}

export function CalendlyWidget({
  url = ExternalUrls.calendly,
  styles = { height: '700px', minWidth: '320px' },
}: CalendlyWidgetProps) {
  useEffect(() => {
    // Load Calendly script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    // Load Calendly CSS
    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    return () => {
      // Cleanup
      document.body.removeChild(script);
      document.head.removeChild(link);
    };
  }, []);

  return (
    <Box
      className="calendly-inline-widget"
      data-url={url}
      sx={{
        minWidth: styles.minWidth,
        height: styles.height,
        width: '100%',
      }}
    />
  );
}

// Popup hook for programmatic control
export function useCalendlyPopup() {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  const openPopup = (url: string = ExternalUrls.calendly) => {
    if (window.Calendly) {
      // @ts-expect-error Calendly popup widget
      window.Calendly.initPopupWidget({ url });
    }
  };

  return { openPopup };
}
